import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Col, Row, Card, CardBody, Input } from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  exportProperties,
  getAllProperties,
  getPropertiesByOrganizationId,
  uploadProperties,
} from '../../services/propertyService';
import CreatePropertyModel from './create-property-model';
import { useDebounce } from '../../hooks/useDebounce';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import { AccessLevels } from 'utility/interface';
import { getFileViewUrl } from 'services/fileService';
import BulkExcelActions, {
  BulkExcelLoadingOverlay,
} from 'Components/Common/BulkExcelActions';
import { useBulkExcelOperations } from 'utility/excelBulkOperations';

const Properties = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [modal, setModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const searchParams = new URLSearchParams(window.location.search);
  const mazarName = searchParams.get('mazar_name');
  const category = searchParams.get('category');

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setEditData(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const loadData = async (search = '') => {
    dispatch(isVisible(true));
    try {
      const authUser = sessionStorage.getItem('authUser');
      if (authUser) {
        const parsedUser = JSON.parse(authUser);
        const result =
          id || !parsedUser.accessLevels.includes(AccessLevels.SUPER_ADMIN)
            ? await getPropertiesByOrganizationId(
                id || parsedUser.organizationId,
                search,
              )
            : await getAllProperties(search);
        if (result?.data?.length) {
          const list = result?.data?.map(property => property);
          setProperties(list);
        } else {
          setProperties([]);
        }
      }
    } catch (error) {
      toast.error(`Error fetching properties: ${error}`);
      console.error('Error fetching properties:', error);
      setProperties([]);
    } finally {
      dispatch(isVisible(false));
    }
  };

  const { loading, handleExport, handleUpload } = useBulkExcelOperations({
    exportFile: () => exportProperties(null, null),
    getExportFilename: () => 'Property.xlsx',
    uploadFile: uploadProperties,
    onUploadSuccess: () => loadData(),
  });

  useEffect(() => {
    loadData(debouncedSearchQuery);
  }, [debouncedSearchQuery, pathname]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <React.Fragment>
      {loading && <BulkExcelLoadingOverlay />}
      <div className="page-content">
        <ToastContainer closeButton={false} limit={1} />
        <BreadCrumb
          title="Properties"
          breadcrumbItems={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: mazarName || 'Mazar', isActive: true },
          ]}
        />
        <Row className="g-4 mb-3">
          <div className="col-sm-auto">
            <div>
              <button
                type="button"
                className="btn btn-primary add-btn"
                onClick={toggle}
              >
                <i className="ri-add-line align-bottom me-1"></i> Add Property
              </button>
            </div>

            <BulkExcelActions
              helpTargetId="properties-bulk-upload-help"
              onDownload={handleExport}
              onUpload={handleUpload}
              disabled={loading}
              downloadDisabled={loading || properties.length < 1}
            />
          </div>
          <div className="col-sm-3 ms-auto">
            <div className="d-flex justify-content-sm-end gap-2">
              <div className="search-box ms-2 col-sm-10">
                <Input
                  type="text"
                  className="form-control"
                  placeholder="Search properties"
                  value={searchQuery}
                  onChange={handleSearch}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </div>
          </div>
        </Row>

        <div className="row">
          {properties.map((item, key) => (
            <Col xxl={4} sm={6} key={key} className="project-card">
              <Card
                className="card-height-100 overflow-hidden cursor-pointer"
                key={key}
                onClick={() =>
                  navigate(
                    `/properties/${item.id}` +
                      `?mazar_name=${encodeURIComponent(
                        mazarName ||
                          item.organizationDetails?.organization ||
                          '',
                      )}` +
                      `&property_name=${encodeURIComponent(item.name || '')}` +
                      `&mazar_id=${id || item.organizationId || ''}` +
                      `&category=${category || ''}` +
                      `&trustRegNumber=${encodeURIComponent(
                        item.trustRegNumber || '',
                      )}` +
                      `&landId=${item.landId || ''}` +
                      `&surveyNumber=${encodeURIComponent(
                        item.surveyNumber || '',
                      )}`,
                  )
                }
              >
                <CardBody>
                  <div className="d-flex flex-column h-100">
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted mb-4"></p>
                      </div>
                    </div>
                    <div className="d-flex mb-2">
                      <div className="flex-shrink-0 me-3">
                        <div className="avatar-sm">
                          <div className="avatar-title bg-light rounded">
                            {item.photos?.split(',')?.length > 0 ? (
                              <img
                                src={getFileViewUrl(item.photos.split(',')[0])}
                                alt="property-photo"
                                className="img-fluid p-1"
                              />
                            ) : (
                              <i className="ri-building-2-line fs-20 text-primary"></i>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1 overflow-hidden">
                        <h5 className="mb-1 fs-15 text-truncate">
                          <Link to="#" className="text-body" title={item.name}>
                            {item.name}
                          </Link>
                        </h5>
                        <p className="text-muted text-truncate-two-lines mb-3">
                          {item.address}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </div>

        <CreatePropertyModel
          isOpen={modal}
          toggle={toggle}
          organizationId={Number(id)}
          organizationName={mazarName || ''}
          onSuccess={loadData}
          editData={editData}
        />
      </div>
    </React.Fragment>
  );
};

export default Properties;
